import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, IconButton, Typography } from "@mui/material";
import { Calendar, CloudUpload, Plus } from "lucide-react";
import { useSelector } from "react-redux";
import { selectCanManagePublisherReportingPlacements } from "../../../redux/authSlice";
import moment from "moment";
import { ApiCall } from "../../../helper/axios";
import CommonDrawerLayout from "../../../common_components/CommonDrawerLayout";
import MetricCard from "../../../common_components/MetricCard";
import CommonTabs from "../../../common_components/CommonTabs";
import CommonTable from "../../../common_components/CommonTable";
import TableToolbar from "../../../common_components/TableToolbar";
import Pagination from "../../../common_components/Pagination";
import AppButton from "../../../common_components/AppButton";
import CommonLoader from "../../../common_components/CommonLoader";
import { toast } from "../../../common_components/Toast/ToastUtils";
import HoverTooltip from "../../../common_components/hoverOverlay/HoverTooltip";
import { Dropdown } from "../../../common_components/FormInputs";
import AmBadges from "../../CommissionTracker/components/AmBadges";
import PlacementRowActions from "./PlacementRowActions";
import ConfirmActionModal from "../../../common_components/ConfirmActionModal";
import { NETWORK_METADATA_MAP } from "../../../utils/networkConstants";
import { exportBreakdownExcel, exportPlacementsExcel } from "../exportPublisherReportingDrawerExcel";
const MODULE_KEY = "publisher_reporting";
const BREAKDOWN_LABEL = {
    publisher: "Client Breakdown",
    client: "Publisher Breakdown",
};
// What the breakdown rows / first column are called in each view
const BREAKDOWN_ENTITY = {
    publisher: "Client",
    client: "Publisher",
};
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100].map((n) => ({ value: String(n), label: String(n) }));
const PLACEMENT_COLUMNS = [
    { id: "datePublished", label: "Date Published", width: "140px", align: "left", sortable: true },
    { id: "publication", label: "Publication", width: "135px", align: "left", sortable: true },
    { id: "contact", label: "Contact", width: "135px", align: "left", sortable: true },
    { id: "category", label: "Category", width: "115px", align: "left", sortable: true },
    { id: "type", label: "Type", width: "90px", align: "left", sortable: true },
    { id: "rate", label: "Comm Rate", width: "110px", align: "left", sortable: true },
    { id: "tags", label: "Tags", width: "105px", align: "left", sortable: true },
];
// 3-dots menu column (Duplicate / Delete): only for users who can manage placements. No header text.
const PLACEMENT_ACTION_COLUMN = { id: "actions", label: "", width: "50px", align: "center", sortable: false };
// By Publisher drawer only: the publisher names of the client that are part of the publisher (comma separated)
const INCLUDED_PUBLISHERS_COLUMN = { id: "includedPublishers", label: "Included Publisher(s)", width: "200px", align: "left", sortable: false };
// Column widths in both tables add up to less than the drawer content width so the
// tables fit without a horizontal scrollbar
const getBreakdownColumns = (view) => [
    { id: "name", label: BREAKDOWN_ENTITY[view], width: "130px", align: "left", sortable: true },
    ...(view === "publisher" ? [INCLUDED_PUBLISHERS_COLUMN] : []),
    // All networks of the row combined (icons)
    { id: "networks", label: "Network", width: "90px", align: "left", sortable: false },
    { id: "placements", label: "Placements", width: "90px", align: "left", sortable: true },
    { id: "clicks", label: "Clicks", width: "80px", align: "left", sortable: true },
    { id: "revenue", label: "Revenue", width: "110px", align: "left", sortable: true },
    { id: "orders", label: "Orders", width: "70px", align: "left", sortable: true },
    { id: "spend", label: "Spend", width: "100px", align: "left", sortable: true },
    { id: "roas", label: "ROAS", width: "60px", align: "left", sortable: true },
];
// Tables scroll inside themselves (sticky header) once there are many rows. The space above the table
// (header, cards, tabs, heading, controls) is ~580px; never shorter than 280px.
const TABLE_MAX_HEIGHT = "max(600px, calc(100vh - 580px))";
// Tighter cell padding so the content fits the narrower columns
const compactTableSx = {
    "& .reusable-table-cell, & .reusable-table-header-cell": { px: "8px !important" },
};
const fmtNum = (n) => (n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });
const fmtCurr = (n) => (n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtRatio = (n) => (n || 0).toFixed(2);
const TYPE_LABELS = { paid: "Paid", nonpaid: "Non-Paid" };
// commission_rate is a number or free text; empty / 0 shows as "-"
const fmtRate = (rate) => {
    if (rate === "" || rate === null || rate === undefined)
        return "-";
    const n = Number(rate);
    if (Number.isNaN(n))
        return String(rate);
    return n > 0 ? `${n}%` : "-";
};
// "Aug 1 - 19, 2026" / "Jul 28 - Aug 3, 2026" / "Dec 28, 2025 - Jan 3, 2026"
const formatPeriod = (start, end) => {
    if (!start || !end)
        return "";
    const s = moment(start);
    const e = moment(end);
    if (s.year() !== e.year())
        return `${s.format("MMM D, YYYY")} - ${e.format("MMM D, YYYY")}`;
    if (s.month() !== e.month())
        return `${s.format("MMM D")} - ${e.format("MMM D, YYYY")}`;
    return `${s.format("MMM D")} - ${e.format("D, YYYY")}`;
};
// Network label + icon, same lookup as the old detail popup: exact network key only, so a network without an icon
// (e.g. "share_a_sales", whose stored spelling is not "shareasale") falls back to the generic link icon.
const getNetworkMeta = (network) => {
    const lower = String(network || "").toLowerCase();
    const key = lower.replace(/[\s_]+/g, "_");
    const meta = NETWORK_METADATA_MAP[key] || NETWORK_METADATA_MAP[lower];
    if (meta)
        return { label: meta.name, icon: meta.icon };
    return { label: lower ? lower.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "-" };
};
const cellTextSx = { fontSize: "13px !important", color: "#101828", textAlign: "left", width: "100%" };
// Long text: one line with "...", full text in the theme tooltip. maxWidth keeps the cell from growing with the text.
// The tooltip shows only when the text is really cut (a custom `tooltip` with extra info always shows).
// `interactive`: the mouse can move onto the tooltip (it holds clickable content, e.g. link icons)
const TruncatedCell = ({ text, maxWidth, sx, onClick, tooltip, interactive = false }) => {
    const [isCut, setIsCut] = useState(false);
    return (_jsx(HoverTooltip, { title: tooltip ?? (isCut ? text : ""), placement: "top", useOverlay: false, disableInteractive: !interactive, leaveDelay: interactive ? 150 : 0, children: _jsx(Typography, { className: "all-Font-Sizes", noWrap: true, onMouseEnter: (e) => setIsCut(e.currentTarget.scrollWidth > e.currentTarget.clientWidth), 
            // onClick={onClick}
            sx: {
                ...cellTextSx,
                ...sx,
                maxWidth,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                // ...(onClick && { cursor: "pointer", "&:hover": { color: "#026AA2", textDecoration: "underline" } }),
            }, children: text }) }));
};
// Same as the main listing: a link without http(s):// gets https:// before it is opened (first link only if there are several)
const toAbsoluteUrl = (url) => {
    const clean = (url || "").trim().split(/\s+and\s+|,|\s+/i)[0]?.trim() || "";
    if (!clean)
        return "";
    return /^https?:\/\//i.test(clean) ? clean : `https://${clean}`;
};
const openLink = (url) => {
    const absoluteUrl = toAbsoluteUrl(url);
    if (absoluteUrl)
        window.open(absoluteUrl, "_blank", "noopener,noreferrer");
};
// Max text width of a cell = its column width minus the cell padding (8px each side, see compactTableSx)
const cellMax = (column, extraPx = 0) => `calc(${column.width} - ${16 + extraPx}px)`;
const ActiveClientsDrawer = ({ open, onClose, view, rowId, name, startDate, endDate, compareStartDate, compareEndDate, onAddPlacement, initialTab = "breakdown", refreshKey = 0, onDuplicatePlacement, onPlacementDeleted, clientFilter = "", accountManagerFilter = "", outreachSpecialistFilter = "", categoryFilter = "", }) => {
    const token = useSelector((state) => state.auth?.token) || "";
    // Add Placement needs the "Manage Placements" action of Publisher Reporting (Roles & Permission)
    const canManagePlacements = useSelector(selectCanManagePublisherReportingPlacements);
    const [tab, setTab] = useState("breakdown");
    const [search, setSearch] = useState("");
    const [query, setQuery] = useState(""); // debounced search sent to the API
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [sort, setSort] = useState({ columnId: "datePublished", direction: "desc" });
    const [breakdownLoading, setBreakdownLoading] = useState(false);
    const [summary, setSummary] = useState(null);
    const [breakdownRows, setBreakdownRows] = useState([]);
    // The breakdown API returns every row, so its search / sort / paging run in the browser
    const [bSearch, setBSearch] = useState("");
    const [bPage, setBPage] = useState(1);
    const [bLimit, setBLimit] = useState(10);
    const [bSort, setBSort] = useState({ columnId: "revenue", direction: "desc" });
    const [placementsLoading, setPlacementsLoading] = useState(false);
    const [placements, setPlacements] = useState([]);
    const [placementsTotal, setPlacementsTotal] = useState(0);
    // Row clicked in the breakdown table. The Placement Details tab then shows that row's placements:
    // By Publisher drawer -> the clicked client, By Client drawer -> the clicked publisher.
    // null = the drawer's own publisher / client (opening the tab directly).
    const [selected, setSelected] = useState(null);
    const placementType = selected ? (view === "publisher" ? "client" : "publisher") : view;
    const placementId = selected ? selected.id : rowId;
    const placementName = selected ? selected.name : name;
    // Latest-request counters so a slow, older response never overwrites a newer one
    const breakdownReq = useRef(0);
    const placementsReq = useRef(0);
    // Fresh state every time the drawer opens for a publisher / client
    useEffect(() => {
        if (!open)
            return;
        setTab(initialTab);
        setSearch("");
        setQuery("");
        setPage(1);
        setLimit(10);
        setSort({ columnId: "datePublished", direction: "desc" });
        setBSearch("");
        setBPage(1);
        setBLimit(10);
        setBSort({ columnId: "revenue", direction: "desc" });
        setSelected(null);
        setSummary(null);
        setBreakdownRows([]);
        setPlacements([]);
        setPlacementsTotal(0);
    }, [open, rowId, view, initialTab]);
    // Debounce the search box
    useEffect(() => {
        const t = setTimeout(() => {
            setQuery(search.trim());
            setPage(1);
        }, 400);
        return () => clearTimeout(t);
    }, [search]);
    const headers = useMemo(() => ({ Authorization: token, "x-module-key": MODULE_KEY }), [token]);
    // Summary cards + breakdown table
    useEffect(() => {
        if (!open || !rowId)
            return;
        const reqId = ++breakdownReq.current;
        setBreakdownLoading(true);
        ApiCall("POST", "/api/publisher-reporting/breakdown", {
            type: view,
            id: rowId,
            startDate,
            endDate,
            compareStartDate: compareStartDate || "",
            compareEndDate: compareEndDate || "",
            clientId: clientFilter,
            accountManagerId: accountManagerFilter,
            outreachSpecialistId: outreachSpecialistFilter,
            category: categoryFilter,
        }, headers)
            .then((response) => {
            if (reqId !== breakdownReq.current)
                return;
            const data = response?.data;
            if (data?.success) {
                setSummary(data.data.summary);
                setBreakdownRows(data.data.breakdown || []);
            }
            else {
                toast.error(data?.message || "Failed to load breakdown");
            }
        })
            .catch(() => { if (reqId === breakdownReq.current)
            toast.error("Failed to load breakdown"); })
            .finally(() => { if (reqId === breakdownReq.current)
            setBreakdownLoading(false); });
    }, [open, rowId, view, startDate, endDate, compareStartDate, compareEndDate, clientFilter, accountManagerFilter, outreachSpecialistFilter, categoryFilter, headers, refreshKey]);
    // Placement Details table (only fetched while its tab is open)
    const fetchPlacements = useCallback(() => {
        if (!open || !placementId || tab !== "placements")
            return;
        const reqId = ++placementsReq.current;
        setPlacementsLoading(true);
        ApiCall("POST", "/api/publisher-reporting/placements", {
            type: placementType,
            id: placementId,
            // A clicked breakdown row narrows to the drawer's own side: that publisher's placements for the client row
            // (By Publisher drawer) / that client's placements of the publisher row (By Client drawer)
            ...(selected && (view === "publisher" ? { publisherId: rowId } : { clientId: rowId })),
            startDate,
            endDate,
            filterClientId: clientFilter,
            accountManagerId: accountManagerFilter,
            outreachSpecialistId: outreachSpecialistFilter,
            category: categoryFilter,
            search: query,
            page,
            limit,
            sortBy: sort.columnId,
            sortOrder: sort.direction,
        }, headers)
            .then((response) => {
            if (reqId !== placementsReq.current)
                return;
            const data = response?.data;
            if (data?.success) {
                setPlacements(data.data.placements || []);
                setPlacementsTotal(data.data.total || 0);
            }
            else {
                toast.error(data?.message || "Failed to load placements");
            }
        })
            .catch(() => { if (reqId === placementsReq.current)
            toast.error("Failed to load placements"); })
            .finally(() => { if (reqId === placementsReq.current)
            setPlacementsLoading(false); });
    }, [open, placementId, tab, placementType, view, rowId, selected, startDate, endDate, clientFilter, accountManagerFilter, outreachSpecialistFilter, categoryFilter, query, page, limit, sort, headers, refreshKey]);
    useEffect(() => {
        fetchPlacements();
    }, [fetchPlacements]);
    const drawerTabs = [
        { label: BREAKDOWN_LABEL[view], value: "breakdown" },
        { label: "Placement Details", value: "placements" },
    ];
    const breakdownColumns = useMemo(() => getBreakdownColumns(view), [view]);
    const resetPlacementControls = () => {
        setSearch("");
        setQuery("");
        setPage(1);
        setLimit(10);
        setSort({ columnId: "datePublished", direction: "desc" });
        setPlacements([]);
        setPlacementsTotal(0);
    };
    // Opening the Placement Details tab from the tab bar shows the drawer's own publisher / client
    const handleTabChange = (next) => {
        if (next === "placements" && tab !== "placements") {
            setSelected(null);
            resetPlacementControls();
        }
        setTab(next);
    };
    // Clicking a client / publisher name in the breakdown opens that row's placements
    const handleBreakdownRowClick = (row) => {
        // By Publisher rows are clients (placements are looked up by the client), By Client rows are publishers
        const targetId = view === "publisher" ? row?.clientId : row?.id;
        if (!targetId)
            return;
        setSelected({ id: String(targetId), name: row.name });
        resetPlacementControls();
        setTab("placements");
    };
    // Breakdown: search (name) -> sort -> page
    const breakdownFiltered = useMemo(() => {
        const q = bSearch.trim().toLowerCase();
        const base = q ? breakdownRows.filter((r) => String(r.name || "").toLowerCase().includes(q)) : breakdownRows;
        const key = bSort.columnId;
        return [...base].sort((a, b) => {
            const cmp = key === "name"
                ? String(a.name || "").localeCompare(String(b.name || ""))
                : (Number(a[key]) || 0) - (Number(b[key]) || 0);
            return bSort.direction === "asc" ? cmp : -cmp;
        });
    }, [breakdownRows, bSearch, bSort]);
    const breakdownPaged = useMemo(() => breakdownFiltered.slice((bPage - 1) * bLimit, bPage * bLimit), [breakdownFiltered, bPage, bLimit]);
    // Drawer Export: follows the selected tab
    // - Breakdown: the breakdown rows in the current sort / search. The breakdown API already
    //   returns every row, so no extra request; the Total Summary is left out while a search narrows the rows.
    // - Placement Details: every placement matching the current search / sort (not only the page on screen)
    const [exporting, setExporting] = useState(false);
    const handleExport = async () => {
        if (exporting)
            return;
        if (tab === "breakdown") {
            exportBreakdownExcel(breakdownFiltered, bSearch.trim() ? undefined : summary, {
                view,
                name,
                startDate,
                endDate,
            });
            return;
        }
        if (!placementId)
            return;
        setExporting(true);
        try {
            const response = await ApiCall("POST", "/api/publisher-reporting/placements/export", {
                type: placementType,
                id: placementId,
                ...(selected && (view === "publisher" ? { publisherId: rowId } : { clientId: rowId })),
                startDate,
                endDate,
                filterClientId: clientFilter,
                accountManagerId: accountManagerFilter,
                outreachSpecialistId: outreachSpecialistFilter,
                category: categoryFilter,
                search: query,
                sortBy: sort.columnId,
                sortOrder: sort.direction,
            }, headers);
            const data = response?.data;
            if (!data?.success) {
                toast.error(data?.message || "Failed to export placements");
                return;
            }
            exportPlacementsExcel(data.data.placements, { name, subject: placementName, startDate, endDate });
        }
        catch (err) {
            toast.error(err?.response?.data?.message || "Failed to export placements");
        }
        finally {
            setExporting(false);
        }
    };
    // "Show N entries" + search box, shared by both tabs
    const renderListControls = (limitValue, onLimit, searchValue, onSearch, placeholder) => (_jsxs(Box, { sx: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5, flexWrap: "wrap" }, children: [_jsxs(Box, { sx: { display: "flex", alignItems: "center", gap: 1 }, children: [_jsx(Typography, { className: "all-Font-Sizes", sx: { fontSize: "13px !important", color: "#475467" }, children: "Show" }), _jsx(Box, { sx: { width: "96px" }, children: _jsx(Dropdown, { value: String(limitValue), options: PAGE_SIZE_OPTIONS, onChange: (v) => onLimit(Number(v)), size: "sm", fullWidth: true, sortByLabel: false, usePortal: true }) }), _jsx(Typography, { className: "all-Font-Sizes", sx: { fontSize: "13px !important", color: "#475467" }, children: "entries" })] }), _jsx(Box, { sx: {
                    width: "300px",
                    maxWidth: "100%",
                    "& .table-toolbar-container": { p: 0 },
                    "& .table-toolbar-title-section": { display: "none" },
                }, children: _jsx(TableToolbar, { title: "", showSearch: true, showFullScreen: false, alwaysOpenSearch: true, searchValue: searchValue, onSearchChange: onSearch, searchPlaceholder: placeholder }) })] }));
    const primaryPeriod = formatPeriod(startDate, endDate);
    // Summary cards (a dash while loading)
    const cardValue = (value, kind) => {
        if (breakdownLoading || !summary)
            return "-";
        if (kind === "curr")
            return fmtCurr(value || 0);
        if (kind === "ratio")
            return fmtRatio(value || 0);
        return fmtNum(value || 0);
    };
    const summaryCards = [
        { title: "Placements", value: cardValue(summary?.placements, "num") },
        { title: "Clicks", value: cardValue(summary?.clicks, "num") },
        { title: "Revenue", value: cardValue(summary?.revenue, "curr"), prefix: "$ " },
        { title: "Orders", value: cardValue(summary?.orders, "num") },
        { title: "Spend", value: cardValue(summary?.spend, "curr"), prefix: "$ " },
        { title: "ROAS", value: cardValue(summary?.roas, "ratio") },
    ];
    const placementRows = useMemo(() => placements.map((p) => ({
        id: p.id,
        raw: p,
        datePublished: p.publishedDate ? moment.utc(p.publishedDate).format("MMM D, YYYY") : "-",
        publication: p.publication || "-",
        title: p.title || "",
        articleLink: p.articleLink || "",
        contact: p.contactName || "-",
        contactEmail: p.contactEmail || "",
        am: p.accountManager || null,
        category: p.category || "-",
        type: TYPE_LABELS[p.type] || p.type || "-",
        rate: fmtRate(p.commissionRate),
        tags: (p.tags || []).join(", ") || "-",
    })), [placements]);
    const placementColumns = useMemo(() => (canManagePlacements ? [...PLACEMENT_COLUMNS, PLACEMENT_ACTION_COLUMN] : PLACEMENT_COLUMNS), [canManagePlacements]);
    // Row actions of the Placement Details table
    // Duplicate: the parent opens the Add Placement drawer pre-filled with this placement (saving creates a new one)
    const handleDuplicatePlacement = (row) => onDuplicatePlacement?.(row.raw);
    // Delete: asks for confirmation first, then calls the delete API
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const handleDeletePlacement = (row) => setDeleteTarget(row);
    const handleDeleteConfirm = async () => {
        if (!deleteTarget || deleting)
            return;
        setDeleting(true);
        try {
            const response = await ApiCall("POST", "/api/publisher-reporting/delete-placement", { id: deleteTarget.id }, headers);
            const data = response?.data;
            if (data?.success) {
                toast.success("Placement deleted successfully.");
                setDeleteTarget(null);
                // the parent reloads the list (counts) and bumps refreshKey, which reloads this drawer's cards / tables
                onPlacementDeleted?.();
            }
            else {
                toast.error(data?.message || "Failed to delete placement");
            }
        }
        catch (err) {
            toast.error(err?.response?.data?.message || "Failed to delete placement");
        }
        finally {
            setDeleting(false);
        }
    };
    const renderPlacementCell = (row, column) => {
        switch (column.id) {
            case "datePublished":
                // date on the left, the AM initials chip on the right (only when an AM is stored on the placement)
                return (_jsxs(Box, { sx: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, minWidth: 0 }, children: [_jsx(TruncatedCell, { text: row.datePublished, maxWidth: cellMax(column, row.am ? 40 : 0), sx: { color: "#101828", width: "auto" } }), row.am && _jsx(AmBadges, { managers: [row.am] })] }));
            case "type":
            case "rate":
                return _jsx(TruncatedCell, { text: row[column.id], maxWidth: cellMax(column), sx: { color: "#475467" } });
            case "publication":
                // publication name + a link icon (only when an article link is saved) that opens the article in a new tab
                return (_jsxs(Box, { sx: { display: "flex", alignItems: "center", gap: 0.25, minWidth: 0 }, children: [_jsx(TruncatedCell, { text: row.publication, maxWidth: cellMax(column, row.articleLink ? 26 : 0), sx: { fontWeight: 500, width: "auto" }, tooltip: _jsxs(Box, { sx: { display: "flex", flexDirection: "column", gap: 0.5 }, children: [_jsxs(Box, { children: [_jsx("b", { children: "Publication:" }), " ", row.publication] }), row.title && _jsxs(Box, { children: [_jsx("b", { children: "Placement Title:" }), " ", row.title] })] }) }), row.articleLink && (_jsx(HoverTooltip, { title: "Open Link", placement: "top", useOverlay: false, disableInteractive: true, children: _jsx(IconButton, { size: "small", sx: { p: 0.5, flexShrink: 0, opacity: 0.6, "&:hover": { opacity: 1 } }, onClick: () => openLink(row.articleLink), children: _jsx("img", { src: "/assets/link.svg", alt: "link", style: { width: "14px", height: "14px" } }) }) }))] }));
            case "contact":
                // name on top, email below in a light grey font
                return (_jsxs(Box, { sx: { display: "flex", flexDirection: "column", minWidth: 0 }, children: [_jsx(TruncatedCell, { text: row.contact, maxWidth: cellMax(column), sx: { color: "#101828", fontWeight: 500 } }), row.contactEmail && (_jsx(TruncatedCell, { text: row.contactEmail, maxWidth: cellMax(column), sx: { color: "#98A2B3", fontSize: "12px !important" } }))] }));
            case "category":
                return _jsx(TruncatedCell, { text: row.category, maxWidth: cellMax(column), sx: { color: "#475467" } });
            case "tags":
                return _jsx(TruncatedCell, { text: row.tags, maxWidth: cellMax(column), sx: { color: "#475467" } });
            case "actions":
                return _jsx(PlacementRowActions, { row: row, onDuplicate: handleDuplicatePlacement, onDelete: handleDeletePlacement });
            default:
                return null;
        }
    };
    const renderBreakdownCell = (row, column) => {
        switch (column.id) {
            case "name":
                return _jsx(TruncatedCell, { text: row.name, maxWidth: cellMax(column), sx: { fontWeight: 500 }, onClick: (view === "publisher" ? row.clientId : row.id) ? () => handleBreakdownRowClick(row) : undefined });
            case "includedPublishers": {
                const included = row.includedPublishers || [];
                if (!included.length)
                    return _jsx(Typography, { className: "all-Font-Sizes", sx: cellTextSx, children: "N/A" });
                // First 2 names in the cell + "+N" for the rest
                const shown = included.slice(0, 2).map((p) => p.name).join(", ") + (included.length > 2 ? ` +${included.length - 2}` : "");
                // Theme tooltip: one name per line (top to bottom) + a link icon on the names that have a link.
                // Shown with more than 2 names, or whenever a link exists (so the link stays reachable)
                const hasLink = included.some((p) => p.url);
                return (_jsx(TruncatedCell, { text: shown, maxWidth: cellMax(column), sx: { color: "#475467" }, interactive: true, tooltip: included.length > 2 || hasLink
                        ? (_jsx(Box, { sx: { display: "flex", flexDirection: "column", gap: 0.75 }, children: included.map((p) => (_jsxs(Box, { sx: { display: "flex", alignItems: "center", gap: 0.5 }, children: [_jsx(Box, { children: p.name }), p.url && (_jsx(IconButton, { size: "small", "aria-label": "Open Link", sx: { p: 0.25, flexShrink: 0, opacity: 0.8, "&:hover": { opacity: 1 } }, onClick: () => openLink(p.url), children: _jsx("img", { src: "/assets/link.svg", alt: "link", style: { width: "14px", height: "14px", filter: "brightness(0) invert(1)" } }) }))] }, p.name))) }))
                        : undefined }));
            }
            case "networks": {
                const networks = row.networks || [];
                if (!networks.length)
                    return _jsx(Typography, { className: "all-Font-Sizes", sx: cellTextSx, children: "N/A" });
                return (_jsx(Box, { sx: { display: "flex", alignItems: "center", gap: 0.75, maxWidth: "74px", overflow: "hidden" }, children: networks.map((n) => {
                        const meta = getNetworkMeta(n);
                        return (_jsx(HoverTooltip, { title: meta.label, placement: "top", useOverlay: false, disableInteractive: true, children: _jsx("img", { src: meta.icon || "/assets/link.svg", alt: meta.label, onError: (e) => { e.currentTarget.src = "/assets/link.svg"; }, style: { width: 20, height: 20, objectFit: "contain", flexShrink: 0, cursor: "help" } }) }, n));
                    }) }));
            }
            case "placements":
            case "clicks":
            case "orders":
                // placements is null when it can't be split (By Client drawer, per publisher)
                return _jsx(Typography, { className: "all-Font-Sizes", sx: cellTextSx, children: row[column.id] === null || row[column.id] === undefined ? "-" : fmtNum(row[column.id]) });
            case "revenue":
            case "spend":
                return (_jsxs(Typography, { className: "all-Font-Sizes", sx: cellTextSx, children: [_jsx("span", { style: { color: "#98A2B3" }, children: "$ " }), fmtCurr(row[column.id])] }));
            case "roas":
                return _jsx(Typography, { className: "all-Font-Sizes", sx: cellTextSx, children: fmtRatio(row.roas) });
            default:
                return null;
        }
    };
    const isPlacementTab = tab === "placements";
    // The first load of the drawer (cards not here yet): one loader over the whole drawer, whichever tab is open.
    // Reloads with the cards already there (e.g. after adding a placement) keep the content visible.
    const showDrawerLoader = open && breakdownLoading && !summary;
    return (_jsxs(CommonDrawerLayout, { open: open, onClose: onClose, title: _jsxs(Box, { sx: { display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }, children: [_jsx(Box, { children: name || "" }), startDate && endDate && (_jsxs(Box, { sx: {
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "6px",
                        px: 1.25,
                        height: "32px",
                        borderRadius: "8px",
                        bgcolor: "#F9FAFB",
                        border: "1px solid #EAECF0",
                    }, children: [_jsx(Calendar, { size: 15, color: "#71717A" }), _jsxs(Typography, { className: "all-Font-Sizes", sx: { fontSize: "13px !important", fontWeight: 500, color: "#344054", lineHeight: "20px", whiteSpace: "nowrap" }, children: [moment(startDate).format("MMM DD, YYYY"), " - ", moment(endDate).format("MMM DD, YYYY")] })] }))] }), width: "xl", customWidth: "1040px", headerActions: _jsx(AppButton, { variant: "contained", color: "primary", className: "app-common-button", size: "small", onClick: handleExport, loading: exporting, disabled: showDrawerLoader, startIcon: _jsx(CloudUpload, { size: 16 }), sx: { height: "36px", whiteSpace: "nowrap", fontSize: "13px", fontWeight: 500, gap: 1 }, children: "Export" }), children: [_jsxs(Box, { sx: {
                    position: "relative", display: "flex", flexDirection: "column", gap: 2, p: 2, boxSizing: "border-box", bgcolor: "#fff", minHeight: "100%",
                    "& > *": { minWidth: 0 },
                    // While the loader is up the content behind it takes no space, so the drawer has nothing to scroll
                    ...(showDrawerLoader && { "& > :not(.drawer-loader-overlay)": { display: "none" } }),
                }, children: [showDrawerLoader && (_jsx(Box, { className: "drawer-loader-overlay", sx: {
                            position: "absolute",
                            inset: 0,
                            zIndex: 10,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            bgcolor: "#fff",
                        }, children: _jsx(CommonLoader, { className: "table-loader" }) })), _jsx(Box, { sx: { display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 1.5 }, children: summaryCards.map((m) => (_jsx(MetricCard, { title: m.title, value: m.value, prefix: m.value === "-" ? "" : m.prefix, sx: { p: 1.5, minWidth: 0 } }, m.title))) }), _jsxs(Box, { sx: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5, flexWrap: "wrap" }, children: [_jsx(CommonTabs, { selectedValue: tab, onChange: handleTabChange, tabs: drawerTabs }), isPlacementTab && canManagePlacements && (_jsx(AppButton, { variant: "contained", color: "primary", className: "app-common-button", size: "small", onClick: onAddPlacement, startIcon: _jsx(Plus, { size: 16 }), sx: { height: "36px", whiteSpace: "nowrap", fontSize: "13px", fontWeight: 500 }, children: "Add Placement" }))] }), _jsxs(Box, { children: [_jsx(Typography, { className: "all-Font-Sizes", sx: { fontSize: "16px !important", fontWeight: 700, color: "#101828" }, children: isPlacementTab ? `Placement Details - ${placementName || ""}` : BREAKDOWN_LABEL[view] }), _jsx(Typography, { className: "all-Font-Sizes", sx: { fontSize: "12px !important", color: "#667085" }, children: isPlacementTab
                                    ? `Placements for the selected period (${primaryPeriod})`
                                    : `${BREAKDOWN_ENTITY[view]}s for the selected period (${primaryPeriod})` })] }), isPlacementTab ? (_jsxs(_Fragment, { children: [renderListControls(limit, (n) => { setLimit(n); setPage(1); }, search, setSearch, "Search placements..."), _jsxs(Box, { sx: { border: "1px solid #E5E7EB", borderRadius: "8px", overflow: "hidden", bgcolor: "#fff", ...compactTableSx }, children: [_jsx(CommonTable, { columns: placementColumns, rows: placementRows, renderCell: renderPlacementCell, isLoading: placementsLoading, isFirstLoad: placementsLoading && placementRows.length === 0, sortConfig: sort, onSortChange: (colId, dir) => { setSort({ columnId: colId, direction: dir || "desc" }); setPage(1); }, expandTrigger: "none", stickyHeader: true, maxHeight: TABLE_MAX_HEIGHT }), _jsx(Box, { sx: { p: "12px 16px", borderTop: "1px solid #E5E7EB" }, children: _jsx(Pagination, { currentPage: page, totalPages: Math.ceil(placementsTotal / limit), totalEntries: placementsTotal, itemsPerPage: limit, onPageChange: setPage }) })] })] })) : (_jsxs(_Fragment, { children: [renderListControls(bLimit, (n) => { setBLimit(n); setBPage(1); }, bSearch, (v) => { setBSearch(v); setBPage(1); }, `Search ${BREAKDOWN_ENTITY[view].toLowerCase()}s...`), _jsxs(Box, { sx: { border: "1px solid #E5E7EB", borderRadius: "8px", overflow: "hidden", bgcolor: "#fff", ...compactTableSx }, children: [_jsx(CommonTable, { columns: breakdownColumns, rows: breakdownPaged, renderCell: renderBreakdownCell, isLoading: breakdownLoading, isFirstLoad: breakdownLoading && breakdownRows.length === 0, sortConfig: bSort, onSortChange: (colId, dir) => { setBSort({ columnId: colId, direction: dir || "desc" }); setBPage(1); }, expandTrigger: "none", stickyHeader: true, maxHeight: TABLE_MAX_HEIGHT }), _jsx(Box, { sx: { p: "12px 16px", borderTop: "1px solid #E5E7EB" }, children: _jsx(Pagination, { currentPage: bPage, totalPages: Math.ceil(breakdownFiltered.length / bLimit), totalEntries: breakdownFiltered.length, itemsPerPage: bLimit, onPageChange: setBPage }) })] })] }))] }), _jsx(ConfirmActionModal, { open: Boolean(deleteTarget), title: "Delete placement", description: `"${deleteTarget?.publication && deleteTarget.publication !== "-" ? deleteTarget.publication : "This placement"}" will be deleted permanently, This cannot be undone.`, confirmText: "Delete", isDeleteIcon: true, loading: deleting, onConfirm: handleDeleteConfirm, onClose: () => setDeleteTarget(null) })] }));
};
export default ActiveClientsDrawer;
